import { Bell } from "lucide-react";
import ElectorateFinder from "../ElectorateFinder";

export function ElectionNotificationsSection({
  isDark, surface, text, textSec, borderC, shadowMd,
  email, setEmail, selectedElections, setSelectedElections,
  upcomingElections, reminderStatus, submitReminder,
  pushPermission, pushSubscribed, pushLoading,
  enablePushNotifications, disablePushNotifications,
}) {
  return (
    <section className="py-12 px-4" data-testid="find-remind-section" style={{ backgroundColor: isDark ? "#0F172A" : "#F1F5F9" }}>
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
        <ElectorateFinder />

        {/* Email Reminder */}
        <div style={{ background: surface, border: `1px solid ${borderC}`, borderRadius: 16, boxShadow: shadowMd, padding: "24px" }} data-testid="reminder-signup">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: "rgba(13,92,58,0.10)", border: "1px solid rgba(13,92,58,0.18)" }}>
              <Bell style={{ width: 15, height: 15, color: "#0D5C3A" }} />
            </div>
            <h3 className="font-bold text-lg tracking-tight" style={{ fontFamily: "Outfit, sans-serif", color: text }}>Election Reminders</h3>
          </div>
          <p className="text-sm mb-4" style={{ color: textSec }}>Get email reminders before enrolment closes and on election day.</p>
          <input
            type="email"
            placeholder="your@email.com"
            value={email}
            data-testid="reminder-email-input"
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-3 text-sm font-medium transition-all focus:outline-none focus:ring-2 mb-3 rounded-xl"
            style={{ background: isDark ? "rgba(15,23,42,0.8)" : "#F8FAFC", border: `1px solid ${borderC}`, color: text }}
          />
          <p className="text-xs font-semibold mb-2" style={{ color: textSec }}>Select elections:</p>
          <div className="flex flex-wrap gap-1.5 mb-4">
            {upcomingElections.slice(0, 5).map((e) => {
              const selected = selectedElections.includes(e.id);
              return (
                <button
                  key={e.id}
                  data-testid={`reminder-elect-${e.id}`}
                  onClick={() => setSelectedElections(selected ? selectedElections.filter((x) => x !== e.id) : [...selectedElections, e.id])}
                  className="text-xs px-3 py-1.5 rounded-full font-medium transition-all duration-200"
                  style={{ background: selected ? "#0D5C3A" : isDark ? "rgba(30,41,59,0.7)" : "#F1F5F9", color: selected ? "#fff" : textSec, border: `1px solid ${selected ? "transparent" : borderC}` }}
                >
                  {e.state || "FED"} {new Date(e.date).getFullYear()}
                </button>
              );
            })}
          </div>
          <button
            data-testid="subscribe-reminder-btn"
            onClick={submitReminder}
            className="w-full py-3 font-semibold text-sm rounded-xl transition-all hover:-translate-y-0.5 active:scale-[0.98]"
            style={{ background: "linear-gradient(135deg, #0D5C3A, #10B981)", color: "#fff", boxShadow: "0 6px 18px rgba(13,92,58,0.24)" }}
          >
            Subscribe to Reminders
          </button>
          {reminderStatus && (
            <p className="text-xs mt-2 font-medium" style={{ color: reminderStatus.includes("Subscribed") ? "#10B981" : "#E11D48" }} data-testid="reminder-status">
              {reminderStatus}
            </p>
          )}
        </div>

        {/* Push Notifications */}
        <div style={{ background: surface, border: `1px solid ${borderC}`, borderRadius: 16, boxShadow: shadowMd, padding: "24px" }} data-testid="push-notification-section">
          <div className="flex items-center gap-2.5 mb-3">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: "rgba(139,92,246,0.10)", border: "1px solid rgba(139,92,246,0.18)" }}>
              <Bell style={{ width: 15, height: 15, color: "#8B5CF6" }} />
            </div>
            <h3 className="font-bold text-lg tracking-tight" style={{ fontFamily: "Outfit, sans-serif", color: text }}>Push Notifications</h3>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: "rgba(139,92,246,0.10)", color: "#8B5CF6", border: "1px solid rgba(139,92,246,0.18)" }}>NEW</span>
          </div>
          <p className="text-sm mb-4" style={{ color: textSec }}>Get instant browser alerts for election dates, enrolment deadlines, and results.</p>
          {pushPermission === "unsupported" ? (
            <p className="text-xs font-medium" style={{ color: textSec }}>Push notifications are not supported in this browser.</p>
          ) : pushPermission === "denied" ? (
            <p className="text-xs font-medium" style={{ color: "#E11D48" }}>Notifications blocked. Please enable them in your browser settings.</p>
          ) : pushSubscribed ? (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-sm font-medium" style={{ color: "#10B981" }}>
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                Push notifications enabled
              </div>
              <button
                onClick={disablePushNotifications}
                disabled={pushLoading}
                data-testid="disable-push-btn"
                className="text-xs px-3 py-1.5 rounded-lg font-medium transition-all hover:opacity-80 disabled:opacity-60"
                style={{ background: isDark ? "rgba(51,65,85,0.5)" : "#F1F5F9", color: textSec, border: `1px solid ${borderC}` }}
              >
                Disable
              </button>
            </div>
          ) : (
            <button
              onClick={enablePushNotifications}
              disabled={pushLoading}
              data-testid="enable-push-btn"
              className="w-full py-3 font-semibold text-sm rounded-xl transition-all hover:-translate-y-0.5 active:scale-[0.98] disabled:opacity-60"
              style={{ background: "linear-gradient(135deg, #6D28D9, #8B5CF6)", color: "#fff", boxShadow: "0 6px 18px rgba(109,40,217,0.24)" }}
            >
              {pushLoading ? "Setting up..." : "Enable Election Alerts"}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
